import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import PoweredByFleetOps from 'components/PoweredByFleetOps';

import { LeaderboardStep } from './buildSteps';
import DriverLeaderboardTable, {
  LeaderboardDesignColumns,
  LeaderboardDesignRow,
} from './DriverLeaderboardTable';
import { playIn, playOut } from './motion';

/*
 * Presentation + motion for the Driver Leaderboard, matching the handoff design.
 * Rendered inside the 1920×1080 ScalingCanvas, so sizes are design px. The grid
 * body is DriverLeaderboardTable; around it is the title (with a slot-reel phrase
 * per range) with the freshness stamp to its right, and a footer with the
 * rotation progress bars + page indicator (left) and the "Powered By FleetOps"
 * mark (right — the WallBoard skips its screen-pinned copy for this slide
 * type). Rotation steps through every page of every range; a range's bar fills
 * across its pages. Motion is applied via the Web Animations API to
 * [data-anim] nodes (see motion.ts).
 */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  gap: 28px;
  padding: 48px 64px 40px;
  box-sizing: border-box;
  overflow: hidden;
  background: var(--color-leaderboard-surface);
  color: var(--color-leaderboard-ink);
  font-family: 'Geist', ui-sans-serif, system-ui, sans-serif;
  letter-spacing: -0.005em;
`;

// Title sits 8px higher than the root padding alone would put it; the bottom
// margin gives that space back so the table doesn't move.
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  margin: -8px 0 8px;
`;

const Title = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  font-size: 64px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.03em;
  white-space: nowrap;
`;

const Bullet = styled.span`
  color: var(--color-leaderboard-quiet);
`;

const PhraseClip = styled.span`
  display: inline-block;
  overflow: hidden;
  padding: 0.08em 0.1em 0.12em 0;
  margin: -0.08em 0 -0.12em;
  color: var(--color-leaderboard-muted);
`;

const Phrase = styled.span`
  display: inline-block;
`;

const Freshness = styled.span`
  font-size: 26px;
  font-weight: 500;
  color: var(--color-leaderboard-freshness);
  white-space: nowrap;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
`;

const FooterStart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 32px;
`;

const Segments = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
  gap: 12px;
  width: ${(p) => p.$count * 240}px;
  flex: none;
`;

const PageLabel = styled.span`
  font-size: 24px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--color-leaderboard-muted);
  white-space: nowrap;
`;

const Segment = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SegLabel = styled.span<{ $active: boolean }>`
  font-size: 24px;
  text-align: left;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  color: ${(p) =>
    p.$active
      ? 'var(--color-leaderboard-ink)'
      : 'var(--color-leaderboard-muted)'};
  /* Geist is a variable font, so the weight eases between 400 and 600 instead
     of snapping when the active range changes. */
  transition:
    font-weight 600ms ease,
    color 600ms ease;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 999px;
  background: var(--color-leaderboard-border);
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background: var(--color-leaderboard-progress);
`;

const formatUpdatedAt = (updatedAt: number): string => {
  const time = new Date(updatedAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Updated ${time}`;
};

const DriverLeaderboardStage = ({
  slide,
  views,
  steps,
  stepIndex,
  progress,
  isExiting,
  columns,
  rows,
  rowSlots,
  updatedAt,
}: {
  slide: DriverLeaderboardSlide;
  views: DriverLeaderboardDateView[];
  steps: LeaderboardStep[];
  stepIndex: number;
  progress: number;
  isExiting: boolean;
  columns: LeaderboardDesignColumns;
  rows: LeaderboardDesignRow[];
  rowSlots: number;
  updatedAt?: number;
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isRotating = steps.length > 1;
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const activeIndex = step.viewIndex;
  const activeView = views[activeIndex] ?? views[0];
  const nextStep = steps[(stepIndex + 1) % steps.length];
  const showHeader = slide.showTitle !== false;
  // The legacy no-views fallback labels its single range with the slide name,
  // so only configured ranges get the "• {label}" phrase.
  const showPhrase = !!slide.views && slide.views.length > 0;
  const prevViewIndexRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // The title phrase only slot-reels when the range changes, not on a page
    // turn within the same range.
    playIn({
      root: rootRef.current,
      animateTitle: prevViewIndexRef.current !== activeIndex,
    });
    prevViewIndexRef.current = activeIndex;
  }, [stepIndex, activeIndex]);

  const isNextViewChange = nextStep.viewIndex !== activeIndex;
  useEffect(() => {
    if (isRotating && isExiting) {
      playOut({ root: rootRef.current, animateTitle: isNextViewChange });
    }
  }, [isRotating, isExiting, isNextViewChange]);

  return (
    <Root ref={rootRef}>
      {showHeader && (
        <HeaderRow>
          <Title>
            <span>{slide.name}</span>
            {showPhrase && activeView && (
              <>
                <Bullet>•</Bullet>
                <PhraseClip>
                  <Phrase data-anim="title">{activeView.label}</Phrase>
                </PhraseClip>
              </>
            )}
          </Title>
          {updatedAt !== undefined && (
            <Freshness>{formatUpdatedAt(updatedAt)}</Freshness>
          )}
        </HeaderRow>
      )}

      <Body>
        <DriverLeaderboardTable
          columns={columns}
          rows={rows}
          rowSlots={rowSlots}
        />
      </Body>

      <Footer>
        {/* Empty span keeps the mark right-aligned when not rotating */}
        {isRotating ? (
          <FooterStart>
            <Segments $count={views.length}>
              {views.map((view, i) => {
                const fill =
                  i < activeIndex
                    ? 1
                    : i === activeIndex
                      ? (step.pageIndex + progress) / step.pageCount
                      : 0;
                return (
                  <Segment key={view.id}>
                    <SegLabel $active={i === activeIndex}>
                      {view.label}
                    </SegLabel>
                    <Track
                      // The share of the active range's bar this step fills,
                      // for the wallboard screenshot player to animate live
                      // over the still (see PrintWallboardShow).
                      {...(i === activeIndex
                        ? {
                            'data-wallboard-progress-from': String(
                              step.pageIndex / step.pageCount,
                            ),
                            'data-wallboard-progress-to': String(
                              (step.pageIndex + 1) / step.pageCount,
                            ),
                          }
                        : {})}
                    >
                      <Fill style={{ width: `${(fill * 100).toFixed(2)}%` }} />
                    </Track>
                  </Segment>
                );
              })}
            </Segments>
            {step.pageCount > 1 && (
              <PageLabel>{`Page ${step.pageIndex + 1} of ${step.pageCount}`}</PageLabel>
            )}
          </FooterStart>
        ) : (
          <span />
        )}
        <PoweredByFleetOps />
      </Footer>
    </Root>
  );
};

export default DriverLeaderboardStage;
